import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { APPLY_URL } from "../../lib/site-data";
import NetworkBackground from "./NetworkBackground";
import { PriceReveal } from "./PriceReveal";

const HERO_STATS = [
  { value: "6", label: "City Chapters" },
  { value: "9", label: "Professional Circles in each City Chapter", lines: ["Professional Circles in each", "City Chapter"] },
  { value: "1,000+", label: "Members across City Chapters" },
];

const ROTATING_WORDS = ["People", "Opportunities", "Conversations"];

/** Smooth scroll to the given hash target with a premium easing curve.
 *  The native `#anchor` jump is instant and feels abrupt on a marketing
 *  hero — replacing it with a ~800ms eased scroll gives the visitor a
 *  clear "the site is guiding me somewhere" moment.
 *  Falls back to a hash change when reduced-motion is on. */
function smoothScrollTo(hash) {
  const el = document.querySelector(hash);
  if (!el) { window.location.hash = hash; return; }
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  if (prefersReducedMotion) {
    el.scrollIntoView({ behavior: "auto", block: "start" });
    history.replaceState(null, "", hash);
    return;
  }
  const startY = window.scrollY || window.pageYOffset;
  const headerH = window.innerWidth >= 768 ? 80 : 64;
  const maxY = document.documentElement.scrollHeight - window.innerHeight;
  const targetY = Math.min(maxY, Math.max(0, el.getBoundingClientRect().top + startY - headerH));
  const distance = targetY - startY;
  // Duration scales gently with distance so short and long hops feel equally controlled.
  const duration = Math.min(1400, Math.max(900, Math.abs(distance) * 0.9));
  const startTime = performance.now();
  // easeInOutQuart — gentle start, natural momentum, soft settle; no bounce.
  const ease = (t) => (t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2);
  const step = (now) => {
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    window.scrollTo(0, Math.round(startY + distance * ease(t)));
    if (t < 1) requestAnimationFrame(step);
    else history.replaceState(null, "", hash);
  };
  requestAnimationFrame(step);
}

export default function Hero() {
  const [activeWord, setActiveWord] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActiveWord((w) => (w + 1) % ROTATING_WORDS.length), 2000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] flex items-center bg-[#0A0A0A] text-white overflow-hidden"
    >
      <NetworkBackground />

      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 40%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.85) 100%)",
        }}
      />
      <div className="grain" aria-hidden="true" />

      <div className="container-x relative z-10 w-full pt-24 md:pt-20 pb-16 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex flex-col items-center gap-1.5 md:gap-2"
            data-testid="hero-badge"
          >
            <span
              data-testid="hero-positioning-primary"
              className="font-display text-[15px] sm:text-[17px] md:text-[19px] font-bold tracking-[0.06em] md:tracking-[0.08em] text-orange-500 leading-none"
              style={{ textShadow: "0 0 24px rgba(249,115,22,0.35)" }}
            >
              Relationship First
            </span>
            <span
              data-testid="hero-positioning-secondary"
              className="font-display text-[18px] sm:text-[20px] md:text-[23px] font-bold tracking-[0.02em] md:tracking-[0.03em] text-white leading-tight"
            >
              Curated Professional Community
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display mt-7 md:mt-6 text-[30px] sm:text-[36px] md:text-[40px] leading-[1.04] font-extrabold tracking-tightest text-white"
          >
            <span className="block md:inline md:mr-[0.28em]">
              Access to the right
            </span>
            <span
              className="relative inline-flex items-center justify-center md:justify-start overflow-hidden align-baseline"
              data-testid="hero-rotating-word"
              style={{ height: "1.1em", minWidth: "6.3em" }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={ROTATING_WORDS[activeWord]}
                  initial={{ y: "100%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  exit={{ y: "-100%", opacity: 0 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block text-orange-500"
                >
                  {ROTATING_WORDS[activeWord]}
                </motion.span>
              </AnimatePresence>
            </span>
          </h1>

          <div
            data-testid="hero-subheadline"
            className="mt-5 md:mt-4 text-base md:text-xl text-white/70 max-w-2xl md:max-w-[880px] mx-auto leading-relaxed md:text-balance"
          >
            <p>
              The people around you influence the opportunities you see, the
              ideas you discover, and the direction you grow. That&rsquo;s why
              being around the right people matters.
            </p>
          </div>

          {/* Scale / proof stats — static typography, no dividers or cards */}
          <div
            data-testid="hero-stats"
            className="mt-8 md:mt-7 mx-auto grid grid-cols-3 sm:flex sm:items-start sm:justify-center gap-x-2 sm:gap-x-14 md:gap-x-20 max-w-3xl"
          >
            {HERO_STATS.map((s, i) => (
              <div
                key={s.label}
                data-testid={`hero-stat-${i + 1}`}
                className="hfa-stat flex flex-col items-center text-center"
                style={{ animationDelay: `${i * 1.8}s` }}
              >
                <span className="hfa-stat-num font-display text-[30px] sm:text-[36px] md:text-[40px] font-extrabold leading-none tracking-tightest text-white">
                  {s.value}
                </span>
                <span className="hfa-stat-label mt-2 text-[12px] sm:text-[13px] md:text-sm text-white/65 font-medium leading-snug max-w-[120px] sm:max-w-[180px] md:max-w-[240px]">
                  {s.lines
                    ? s.lines.map((ln, j) => (
                        <span key={ln} className={j === s.lines.length - 1 ? "whitespace-nowrap" : undefined}>
                          {ln}{j < s.lines.length - 1 && <br />}
                        </span>
                      ))
                    : s.label}
                </span>
              </div>
            ))}
          </div>

          {/* CTA — button with compact pricing directly beneath */}
          <div
            data-testid="hero-cta-row"
            className="mt-10 md:mt-9 flex flex-col items-stretch sm:items-center justify-center gap-5 md:gap-7"
          >
            <Link
              to={APPLY_URL}
              data-testid="hero-apply-button"
              aria-label="Apply for Membership"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-full font-semibold text-white transition-[transform,background-color,box-shadow] duration-200 ease-out bg-[#EA580C] hover:bg-[#C2410C] active:translate-y-0 hover:-translate-y-[1px] text-[15px] sm:text-[16px] leading-none"
              style={{
                padding: "0.25rem 1.4rem",
                minHeight: "42px",
                border: "1px solid #EA580C",
                boxShadow: "0 6px 20px -8px rgba(234, 88, 12, 0.55)",
              }}
            >
              Apply for Membership
              <ArrowUpRight className="w-4 h-4" />
            </Link>

            <PriceReveal />
          </div>

          <div className="mt-8 md:mt-11 flex flex-col items-stretch sm:items-center justify-center">
            <a
              href="#about"
              data-testid="hero-learn-more-button"
              onClick={(e) => { e.preventDefault(); smoothScrollTo("#about"); }}
              className="hfa-learn-more w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full font-semibold text-white bg-white/[0.05] hover:bg-white/[0.10] border transition-[background-color,border-color,transform] duration-200 ease-out hover:-translate-y-[1px] backdrop-blur-sm border-[rgba(234,88,12,0.45)] hover:border-[rgba(234,88,12,0.75)]"
              style={{
                padding: "0.25rem 1.4rem",
                minHeight: "42px",
                fontSize: "0.95rem",
              }}
            >
              Explore High Five Access
              <ChevronDown className="hfa-learn-more__chev w-4 h-4 text-orange-400" />
            </a>
          </div>
        </motion.div>
      </div>

      <a
        href="#about"
        aria-label="Scroll to about"
        data-testid="hero-scroll-cue"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors"
      >
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </a>
    </section>
  );
}
